import { Platform } from 'react-native';
import { auth } from '../lib/firebase';

const R2_WORKER_URL = process.env.EXPO_PUBLIC_R2_WORKER_URL;

// Chunk size for multipart uploads (5MB - R2 minimum)
const CHUNK_SIZE = 5 * 1024 * 1024;

// File size threshold for multipart upload (10MB)
const MULTIPART_THRESHOLD = 10 * 1024 * 1024;

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

type ProgressCallback = (progress: UploadProgress) => void;

/**
 * Get the current user's Firebase ID token for authentication
 * This is secure because the token is short-lived and verified server-side
 */
async function getAuthToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to upload files');
  }
  return user.getIdToken();
}

/**
 * Upload a file to Cloudflare R2
 * Uses direct upload for small files, multipart for large files (>10MB)
 */
export async function uploadToR2(
  file: Blob | File,
  folder: string = 'uploads',
  onProgress?: ProgressCallback
): Promise<string> {
  if (!R2_WORKER_URL) {
    throw new Error('R2 configuration missing. Check your environment variables.');
  }

  const fileSize = file.size;

  // Use multipart upload for large files
  if (fileSize > MULTIPART_THRESHOLD) {
    return uploadMultipart(file, folder, onProgress);
  }

  // Direct upload for smaller files
  return uploadDirect(file, folder, onProgress);
}

/**
 * Upload a file from a local URI (for React Native)
 * This handles file:// URIs directly without needing to convert to Blob
 */
export async function uploadFromUri(
  uri: string,
  mimeType: string,
  folder: string = 'uploads',
  onProgress?: ProgressCallback
): Promise<string> {
  console.log('[R2] uploadFromUri called with uri:', uri);

  if (!R2_WORKER_URL) {
    throw new Error('R2 configuration missing. Check your environment variables.');
  }

  // Get Firebase auth token
  const authToken = await getAuthToken();

  const formData = new FormData();
  const filename = `video_${Date.now()}.${getExtensionFromMime(mimeType)}`;

  // React Native FormData accepts file-like objects with uri, type, name
  const fileObject = {
    uri: uri,
    type: mimeType,
    name: filename,
  };

  console.log('[R2] File object:', fileObject);
  formData.append('file', fileObject as any);
  formData.append('folder', folder);

  // Report initial progress
  onProgress?.({ loaded: 0, total: 100, percentage: 0 });

  console.log('[R2] Uploading to:', `${R2_WORKER_URL}/upload-direct`);
  const response = await fetch(`${R2_WORKER_URL}/upload-direct`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
    body: formData,
  });

  console.log('[R2] Response status:', response.status);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    console.error('[R2] Upload error:', error);
    throw new Error(error.error || 'Upload failed');
  }

  // Report completion
  onProgress?.({ loaded: 100, total: 100, percentage: 100 });

  const result = await response.json();
  console.log('[R2] Upload result:', result);
  return result.url;
}

/**
 * Direct upload through the Worker (for files <10MB)
 */
async function uploadDirect(
  file: Blob | File,
  folder: string,
  onProgress?: ProgressCallback
): Promise<string> {
  // Get Firebase auth token
  const authToken = await getAuthToken();

  const formData = new FormData();

  // Get filename
  const filename = file instanceof File ? file.name : `file.${getExtensionFromMime(file.type)}`;

  // On React Native, FormData needs the file as an object with uri, type, name
  if (Platform.OS !== 'web') {
    // For native, we need to handle blob URLs differently
    // Create a file-like object that React Native's FormData understands
    const fileObject = {
      uri: (file as any)._data?.blobId
        ? `blob:${(file as any)._data.blobId}`
        : URL.createObjectURL(file),
      type: file.type || 'video/mp4',
      name: filename,
    };
    formData.append('file', fileObject as any);
  } else {
    formData.append('file', file, filename);
  }
  formData.append('folder', folder);

  // Report initial progress
  onProgress?.({ loaded: 0, total: file.size, percentage: 0 });

  const response = await fetch(`${R2_WORKER_URL}/upload-direct`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || 'Upload failed');
  }

  // Report completion
  onProgress?.({ loaded: file.size, total: file.size, percentage: 100 });

  const result = await response.json();
  return result.url;
}

/**
 * Multipart upload for large files (>10MB)
 * Splits file into chunks and uploads in parallel
 */
async function uploadMultipart(
  file: Blob | File,
  folder: string,
  onProgress?: ProgressCallback
): Promise<string> {
  // Get Firebase auth token once for all requests
  const authToken = await getAuthToken();

  const filename = file instanceof File ? file.name : `file.${getExtensionFromMime(file.type || 'video/mp4')}`;
  const contentType = file.type || 'video/mp4';
  const totalSize = file.size;

  // Start multipart upload
  const startResponse = await fetch(`${R2_WORKER_URL}/upload-multipart-start`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ filename, contentType, folder }),
  });

  if (!startResponse.ok) {
    const error = await startResponse.json().catch(() => ({ error: 'Failed to start upload' }));
    throw new Error(error.error || 'Failed to start upload');
  }

  const { key, uploadId } = await startResponse.json();

  // Calculate chunks
  const numChunks = Math.ceil(totalSize / CHUNK_SIZE);
  const parts: { partNumber: number; etag: string }[] = [];
  let uploadedBytes = 0;

  // Upload chunks sequentially (can be parallelized for better speed)
  for (let i = 0; i < numChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalSize);
    const chunk = file.slice(start, end);
    const partNumber = i + 1;

    const partResponse = await fetch(
      `${R2_WORKER_URL}/upload-multipart-part?key=${encodeURIComponent(key)}&uploadId=${encodeURIComponent(uploadId)}&partNumber=${partNumber}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: chunk,
      }
    );

    if (!partResponse.ok) {
      const error = await partResponse.json().catch(() => ({ error: 'Failed to upload part' }));
      throw new Error(error.error || `Failed to upload part ${partNumber}`);
    }

    const partResult = await partResponse.json();
    parts.push({ partNumber: partResult.partNumber, etag: partResult.etag });

    // Update progress
    uploadedBytes += chunk.size;
    onProgress?.({
      loaded: uploadedBytes,
      total: totalSize,
      percentage: Math.round((uploadedBytes / totalSize) * 100),
    });
  }

  // Complete multipart upload
  const completeResponse = await fetch(`${R2_WORKER_URL}/upload-multipart-complete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ key, uploadId, parts }),
  });

  if (!completeResponse.ok) {
    const error = await completeResponse.json().catch(() => ({ error: 'Failed to complete upload' }));
    throw new Error(error.error || 'Failed to complete upload');
  }

  const result = await completeResponse.json();
  return result.url;
}

/**
 * Delete a file from R2
 */
export async function deleteFromR2(key: string): Promise<void> {
  if (!R2_WORKER_URL) {
    throw new Error('R2 configuration missing');
  }

  // Get Firebase auth token
  const authToken = await getAuthToken();

  const response = await fetch(`${R2_WORKER_URL}/delete`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ key }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Delete failed' }));
    throw new Error(error.error || 'Delete failed');
  }
}

/**
 * Extract the R2 key from a public URL
 */
export function getKeyFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    // Remove leading slash
    return urlObj.pathname.slice(1);
  } catch {
    return null;
  }
}

/**
 * Helper to get file extension from MIME type
 */
function getExtensionFromMime(mimeType: string): string {
  const mimeToExt: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/quicktime': 'mov',
  };
  return mimeToExt[mimeType] || 'bin';
}

/**
 * Convert a local URI to a Blob (works on both web and native)
 */
export async function uriToBlob(uri: string): Promise<Blob> {
  if (Platform.OS === 'web' && uri.startsWith('data:')) {
    // Data URL - convert to blob
    const response = await fetch(uri);
    return response.blob();
  }

  // File URI - fetch it
  const response = await fetch(uri);
  return response.blob();
}
